import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from "@angular/common";
import { FormBuilder, FormGroup } from "@angular/forms";
import { GridOptions, ColDef, GridApi, RowSelectionOptions, CheckboxLocation } from "ag-grid-community";
import { Subscription, Observable } from "rxjs";
import { tap, map, switchMap } from "rxjs/operators";

import { Organization } from "../../../../common/models/organization.model";
import { ContactStub } from "../../../models/contact.model";
import { WorkOrderWithAssignments, WorkOrderType } from "../../../../common/models/work-order.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { EDiscoveryService } from "../../../services/eDiscovery.service";``
import { ContactService } from "../../../services/contacts.service";
import { WorkOrderService } from "../../../../common/services/work-order.service";
import { FlowIndex } from "../../../../common/models/flow.dto";
import { PendingFlow } from "../../../../flow/models/flow.model";
import { FlowInstanceService, FlowIndexSpec } from "../../../../flow/services/flow-instance.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { OrganizationService } from "../../../../common/services/organization.service";
import { eDiscoveryRequest } from "../../../models/eDiscovery.model";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { MetadataSearchSpec, ApplicationType } from "../../../../applications/models/applications.models";

@Component({
    selector: 'eDiscovery-new-request',
    templateUrl: './new-request.component.html',
    styleUrls: ['./new-request.component.scss', '../request-wrapper/request-wrapper.component.scss'],
    providers: [EDiscoveryService, FlowInstanceService, ContactService, WorkOrderService],
    standalone: false
})
export class EDiscoveryNewRequestComponent implements OnInit, OnDestroy {

	appGridOptions: GridOptions;
	appGridApi?: GridApi;
    applicationList: FlowIndex[];
    applicationRequiredDialog: Dialog = new Dialog("At least one application is required when including application data or application notes.", [new DialogButton("OK", true, () => this.onDialogClose())]);
    availableOrgs$: Observable<Organization[]>;
    availableOrganizations: Organization[];
    contacts$: Subscription;
    contactList: ContactStub[] = null;
    contactGridOptions: GridOptions;
	contactGridApi?: GridApi;
    @Output() errorOccurred: EventEmitter<any> = new EventEmitter<any>();

    @Input() newRequest: eDiscoveryRequest;
      
    newRequestSubheader: SubHeader = new SubHeader({
        title: "eDiscovery - Create Request",
        buttons: [{ name: "Cancel", method: () => this.cancelNew() }, { name: "Create", method: () => this.createRequest() }]
    });
    noDocumentTypesDialog: Dialog = new Dialog("You have not selected any data to be included in the eDiscovery request.  Please select one or more options to continue.", [new DialogButton("Cancel", false, () => this.onDialogClose())]);

    readonly notifyText: string = "A notification will be sent when it has been completed.";
    private _organization: Organization = null;
    @Input() set organization(value: Organization) {
        if (this._organization != value) {
            this._organization = value;
            this.organizationChanged.emit(this._organization);
        }
    }

    get organization(): Organization {
        return this._organization;
    }

    @Output() organizationChanged: EventEmitter<Organization> = new EventEmitter<Organization>();
	orgGridOptions: GridOptions;
	orgGridApi?: GridApi;
    orgRequiredDialog: Dialog = new Dialog("An organization is required to submit a new request", [new DialogButton("OK", true, () => this.onDialogClose())]);

    @Output() requestCancelled: EventEmitter<eDiscoveryRequest> = new EventEmitter<eDiscoveryRequest>();
    requestForm: FormGroup;
    requestSubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => this.resetForm())]);

    selectedApplications: FlowIndex[] = [];
    selectedContacts: string[] = [];
    selectedWorkOrders: string[] = [];
    singlePageApplicationTypes: string[] = [];
    subscriptions = new Subscription();
    readonly successText: string = "Your request has been successfully submitted.";
    readonly timeFrameText: string = "Please allow up to 24 hours for the request to be completed.";

	workOrderGridOptions: GridOptions;
	workOrderGridApi?: GridApi;
    workOrders$: Subscription;

    workOrderList: WorkOrderWithAssignments[] = null;

    constructor(private _headerService: SubHeaderService,
        private _dialogService: DynamicDialogService,
        private _discoveryService: EDiscoveryService,
        private _instanceService: FlowInstanceService,
        private _contactService: ContactService,
        private _workOrderService: WorkOrderService,
        private _formatter: GridFormatterService,
        private _organizationService: OrganizationService,
        private _builder: FormBuilder,
        private _applicationService: ApplicationsService,
		private _datePipe: DatePipe) {
	}

    ngOnInit() {
        this.setGridOptions();
        this.singlePageApplicationTypes = Object.keys(ApplicationType).map(k => ApplicationType[k]);
        this.startNew(this.organization);
        
        this.availableOrgs$ = this._organizationService.getAll().pipe(
            map((data: Organization[]) => {
                this.availableOrganizations = data.filter(o => o.name !== 'ODNR').sort((a, b) => {
                    if (a.name > b.name) return 1;
                    if (a.name < b.name) return -1;
                    return 0;
                });
                if (this.orgGridApi != null && this.organization != null) {
                    let orgId = this.organization.id;
                    setTimeout((orgId) => {
                        this.orgGridApi.forEachNode((node) => {
                            node.setSelected(node.data.id === orgId);
                        }
                        );
                    }, 500, orgId);
                    
                }
                return this.availableOrganizations;
            })
        );
    }

    ngOnDestroy() {
        if (this.contacts$) this.contacts$.unsubscribe();
        if (this.workOrders$) this.workOrders$.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    protected appSelectionChanged(e: Event): void {
        const tempSelected: FlowIndex[] = [];
        const rows = this.appGridApi.getSelectedRows();
        for (let r = 0; r < rows.length; r++) {
            tempSelected.push(rows[r]);
        }
        this.selectedApplications = tempSelected;
    }

    buildRequestForm(): void {
        this.requestForm = this._builder.group({
            orgSelector: [this.newRequest.organizationId, []],
            includeApplData: [this.newRequest.includeApplicationData, []],
            includeApplNotes: [this.newRequest.includeApplicationNotes, []],
            includeAllContacts: [this.newRequest.includeNotesForAllContacts, []],
            includeAllWorkOrders: [this.newRequest.includeAllWorkOrders, []],
            includeOrganizationNotes: [this.newRequest.includeOrganizationNotes, []],
            notifyWhenComplete: [this.newRequest.notifyWhenComplete, []],
            activeOrgs: [true, []]
        });
        if (this.newRequest.organizationId) {
            this.loadApplicationsForOrganization(this.newRequest.organizationId);
            this.loadWorkOrdersForOrganization(this.newRequest.organizationId);
            this.loadContactsForOrganization(this.newRequest.organizationId);
        }
    }

    cancelNew(): void {
        this.newRequest = null;
        this.requestCancelled.emit(this.newRequest);        
    }

    protected contactSelectionChanged(e: Event): void {
        const tempSelected = [];
        const rows = this.contactGridApi.getSelectedRows();
        for (let r = 0; r < rows.length; r++) {
            tempSelected.push((rows[r] as ContactStub).id);
        }
        this.selectedContacts = tempSelected;
    }

    createRequest(): void {
        let org = this.requestForm.controls["orgSelector"].value;
        if (org && org !== "") {
            let includeApplData = (this.requestForm.controls["includeApplData"].value !== null) ? this.requestForm.controls["includeApplData"].value : false;
            let includeApplNotes = (this.requestForm.controls["includeApplNotes"].value !== null) ? this.requestForm.controls["includeApplNotes"].value : false;
            let checkApplicationLength = includeApplData || includeApplNotes;
            if (!checkApplicationLength || this.selectedApplications.length > 0) {
                let includeAllWorkOrders = (this.requestForm.controls["includeAllWorkOrders"].value !== null) ? this.requestForm.controls["includeAllWorkOrders"].value : false;
                let includeOrgNotes = (this.requestForm.controls["includeOrganizationNotes"].value !== null) ? this.requestForm.controls["includeOrganizationNotes"].value : false;
                let includeAllContacts = (this.requestForm.controls["includeAllContacts"].value !== null) ? this.requestForm.controls["includeAllContacts"].value : false;
                let notify = (this.requestForm.controls["notifyWhenComplete"].value !== null) ? this.requestForm.controls["notifyWhenComplete"].value : false;
                if ((checkApplicationLength && this.selectedApplications.length > 0) || includeAllWorkOrders || this.selectedWorkOrders.length > 0 || includeOrgNotes || includeAllContacts || this.selectedContacts.length > 0) {
                    let applicationNumbers: string = "";
                    let flowIds: string = "";
                    let isSinglePage: boolean = false;
                    if (includeApplData || includeApplNotes) {
                        this.selectedApplications.forEach(app => {
                            if (applicationNumbers.length > 0) {
                                applicationNumbers = `${applicationNumbers},`;
                            }
                            applicationNumbers = `${applicationNumbers}${app.flowIdentifier}`;
                            if (flowIds.length > 0) {
                                flowIds = `${flowIds},`;
                            }
                            flowIds = `${flowIds}${app.flowId}`;
                            isSinglePage = isSinglePage || this.singlePageApplicationTypes.includes(app.name);
                        });
                    }
                    this.newRequest = {
                        organizationId: org,
                        includeApplicationData: includeApplData,
                        includeApplicationNotes: includeApplNotes,
                        includeAllWorkOrders: includeAllWorkOrders,
                        includeOrganizationNotes: includeOrgNotes,
                        includeNotesForAllContacts: includeAllContacts,
                        contacts: this.selectedContacts.join(","),
                        applicationNumbers: applicationNumbers,
                        flowInstanceId: flowIds,
                        workOrders: this.selectedWorkOrders.join(","),
                        notifyWhenComplete: notify,
                        isSinglePageApplication: isSinglePage
                    } as eDiscoveryRequest;
                    let create$ = this._discoveryService.createRequest(this.newRequest).pipe()
                        .subscribe((result) => {
                            this.requestSubmittedDialog.message = `${this.successText}<br />${this.timeFrameText}`;
                            if (result.requestId) {
                                this.requestSubmittedDialog.message = `${this.requestSubmittedDialog.message}<br />Your request id is ${result.requestId}.`;
                            }
                            if (notify) {
                                this.requestSubmittedDialog.message = `${this.requestSubmittedDialog.message}<br />${this.notifyText}`;
                            }
                            this.requestSubmittedDialog.toggleDialog(true);
                            this._dialogService.updateDialog(this.requestSubmittedDialog);
                        },
                            (error) => {
                                console.error(error);
                                this.showError(error);
                            });
                    this.subscriptions.add(create$);
                }
                else {
                    this.noDocumentTypesDialog.toggleDialog(true);
                    this._dialogService.updateDialog(this.noDocumentTypesDialog);
                }
            } else {
                this.applicationRequiredDialog.toggleDialog(true);
                this._dialogService.updateDialog(this.applicationRequiredDialog);
            }

        } else {
            this.orgRequiredDialog.toggleDialog(true);
            this._dialogService.updateDialog(this.orgRequiredDialog);
        }
    }

    private defineAppColumns(): ColDef[] {
        const columnDefs = [
            {
                field: "flowIdentifier",        
                // checkboxSelection: true,
                headerName: "Application #",
                headerTooltip: "Application #",
                maxWidth: 500,
                tooltipField: "flowIdentifier",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            } as ColDef,
            {
                field: "name",
                headerName: "Application Type",
                headerTooltip: "Application Type",
                tooltipField: "name"
            } as ColDef,
            {
                field: "submittedDate",
                headerName: "Date Submitted",
                headerTooltip: "Date Submitted",
                valueGetter: this.getSubmittedDate(),
                filter: "agDateColumnFilter",
                filterParams: this._formatter.defaultDateFilterParams
            },
            {
                field: "lastSubmitDate",
                headerName: "Last Submitted On",
                headerTooltip: "Last Submitted On",
                valueGetter: this._formatter.formatDate("lastSubmitDate"),
                filter: "agDateColumnFilter",
                filterParams: this._formatter.defaultDateFilterParams
            },
            {
                field: "county",
                headerName: "County",
                headerTooltip: "County",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            }
        ] as ColDef[];
        this._formatter.applyFilterKeyboardHintTooltips(columnDefs);
        return columnDefs;
    }

    private defineContactColumns(): ColDef[] {
        const columnDefs = [
            {
                field: "displayName",
                // headerCheckboxSelection: true,
                headerName: "Contact",
                headerTooltip: "Contact",
                maxWidth: 500,
                // checkboxSelection: true
            } as ColDef,
            {
                field: "emailAddress",
                headerName: "E-mail Address",
                headerTooltip: "E-mail Address",
                maxWidth: 500,
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            } as ColDef
        ] as ColDef[];
        this._formatter.applyFilterKeyboardHintTooltips(columnDefs);
        return columnDefs;
    }

    private defineOrganizationColumns(): ColDef[] {
        const columnDefs = [
            {
                // checkboxSelection: true,
                field: "name",
                headerName: "Organization Name",
                headerTooltip: "Organization Name",
                maxWidth: 500,
                tooltipField: "name",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            } as ColDef,
            {
                field: "id",
                headerName: "RICS Owner Number",
                headerTooltip: "RICS Owner Number",
                tooltipField: "id"
            } as ColDef,
            {
                field: "historicalOwnerNumber",
                headerName: "RBDMS Owner Number",
                headerTooltip: "RBDMS Owner Number",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            }
        ] as ColDef[];
        this._formatter.applyFilterKeyboardHintTooltips(columnDefs);
        return columnDefs;
    }

    private defineWorkOrderColumns(): ColDef[] {
        const columnDefs = [
            {
                field: "id",
                // headerCheckboxSelection: true,
                maxWidth: 500,
                // checkboxSelection: true
            } as ColDef,
            {
                field: "title",
                tooltipField: "title",
                sortable: true,
                headerName: "Title",
                headerTooltip: "Title",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            }
        ] as ColDef[];
        this._formatter.applyFilterKeyboardHintTooltips(columnDefs);
        return columnDefs;
    }

    getSubmittedDate(): any {
        return (obj) => {
            let date: any = null;
            if (obj != null && obj.data != null) {
                if (this._formatter.isNullUndefinedOrEmpty(obj.data["submittedDate"]) == false && obj.data["submittedDate"] != "{}") {
                    date = this._datePipe.transform(obj.data["submittedDate"], "MM/dd/yyyy");
                } else if (this._formatter.isNullUndefinedOrEmpty(obj.data["dateSubmitted"]) == false && obj.data["dateSubmitted"] != "{}") {
                    date = this._datePipe.transform(obj.data["dateSubmitted"], "MM/dd/yyyy");
                }
            }
            return date;
        }
    }

    private loadApplicationsForOrganization(organizationId: string): void {
        if (organizationId && organizationId !== "") {
            const spec = new FlowIndexSpec();
            spec.organizationId = organizationId;
            spec.ownedFlowsOnly = true;
            spec.isSubmitted = true;
            let flow$ = this._instanceService.findFlowIndicies(spec).pipe(
                map((data) => {
                    let filtered = data.filter(d => d.flowIdentifier !== undefined && d.flowIdentifier !== null && d.flowIdentifier !== "");
                    return filtered;
                }));
            const metaSpec = {
                organizationId : organizationId
            } as MetadataSearchSpec;
            let single$ = this._applicationService.searchMetadata(metaSpec).pipe(               
            );

            const applications$ = flow$
                .pipe(
                    switchMap((flows: FlowIndex[]) => {
                        return single$.pipe(
                            map((workflows: PendingFlow[]) => {
                                return flows.concat(workflows);
                            })
                        )
                    })
            ).subscribe((apps: FlowIndex[]) => {
                this.applicationList = apps;
                    if (this.appGridApi) {
						this.appGridApi.sizeColumnsToFit();
                    }
            },
                (error) => {
                    console.error(error);
                    this.showError(error);
                });
            this.subscriptions.add(applications$);
        }
    }

    private loadContactsForOrganization(organizationId: string): void {
        if (organizationId && organizationId !== "") {
            this.contacts$ = this._contactService.getContactsByOrganization(organizationId).pipe(tap((data) => {
                this.contactList = data;
            }))
                .subscribe(() => {
                    if (this.contactGridApi) {
						this.contactGridApi.sizeColumnsToFit();
                    }
                },
                    (error) => {
                        console.error(error);
                        this.showError(error);
                    }
                );
        }
    }

    private loadWorkOrdersForOrganization(organizationId: string): void {
        if (organizationId && organizationId !== "") {
            this.workOrders$ = this._workOrderService.getOrganizationWorkOrders(organizationId, [WorkOrderType.AdHoc]).pipe(tap((data) => {
                this.workOrderList = data;
            })).subscribe(() => {
				if (this.workOrderGridApi) {
					this.workOrderGridApi.sizeColumnsToFit();
                }
            },
                (error) => {
                    console.error(error);
                    this.showError(error);
                }
            );
        }
    }

    onDialogClose(): void {
        
    }

    orgSelectionChanged(evt: any): void {
        const rows = this.orgGridApi.getSelectedRows();
        let orgId = "";
        switch (rows.length) {
            case 0:
                orgId = "";
                break;
            case 1:
                orgId = rows[0].id;
                break;
            default:
                console.error("somehow have more than one selected row in the grid");
                return;
                break;
        }
        let orgCtrl = this.requestForm.get("orgSelector");
        if (orgCtrl != null) {
            orgCtrl.setValue(orgId);
            this.organization = this.availableOrganizations.find(o => o.id === orgId);
        }
        this.validateRequestForm();
    }

    resetForm(): void {
        this.contactList = null;
        this.workOrderList = null;
        this.applicationList = null;
        this.selectedApplications = [];
        this.selectedContacts = [];
        this.selectedWorkOrders = [];
        this.organization = null;
        if (this.orgGridApi) {
			this.orgGridApi.deselectAll();
			this.orgGridApi.setFilterModel(null);
        }
        if (this.appGridApi) {
			this.appGridApi.deselectAll();
			this.appGridApi.setFilterModel(null);
        }
        if (this.contactGridApi) {
			this.contactGridApi.deselectAll();
			this.contactGridApi.setFilterModel(null);
        }
        if (this.workOrderGridApi) {
			this.workOrderGridApi.deselectAll();
			this.workOrderGridApi.setFilterModel(null);
        }
        this.startNew(null);        
    }

    private setGridOptions(): void {
        this.appGridOptions = {
            columnDefs: this.defineAppColumns(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.appGridApi = params.api;
				this.appGridApi.sizeColumnsToFit();
            },
            rowSelection: {
                mode: "singleRow",
                checkboxes: true,
                checkboxLocation: "selectionColumn",
                headerCheckbox: false
            } as RowSelectionOptions,
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true
        } as GridOptions;

        this.contactGridOptions = {
            columnDefs: this.defineContactColumns(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.contactGridApi = params.api;
				this.contactGridApi.sizeColumnsToFit();
            },
            rowSelection: {
                mode: "multiRow",
                checkboxes: true,
                checkboxLocation: "selectionColumn",
                headerCheckbox: true
            } as RowSelectionOptions,
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true
        } as GridOptions;

        this.orgGridOptions = {
            columnDefs: this.defineOrganizationColumns(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.orgGridApi = params.api;
				this.orgGridApi.sizeColumnsToFit();               
            },
            rowSelection: {
                mode: "singleRow",
                checkboxes: true,
                checkboxLocation: "selectionColumn",
                headerCheckbox: false
            } as RowSelectionOptions,
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true
        }

        this.workOrderGridOptions = {
            columnDefs: this.defineWorkOrderColumns(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            rowSelection: {
                mode: "multiRow",
                checkboxes: true,
                checkboxLocation: "selectionColumn",
                headerCheckbox: true
            } as RowSelectionOptions,
			onGridReady: (params) => {
				this.workOrderGridApi = params.api;
				this.workOrderGridApi.sizeColumnsToFit();
            },
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true
        } as GridOptions;
    }

    startNew(orgToUse: Organization | null): void {
        this._headerService.updateSubHeader(this.newRequestSubheader); 
        this.organization = orgToUse;        
        this.newRequest = {
            organizationId: "",
            applicationNumbers: "",
            contacts: "",
            includeNotesForAllContacts: false,
            includeApplicationData: false,
            includeApplicationNotes: false,
            includeOrganizationNotes: false,
            includeAllWorkOrders: false,
            notifyWhenComplete: false,
            workOrders: ""
        } as eDiscoveryRequest;
        if (orgToUse) {            
            let id = this.organization.id
            this.newRequest.organizationId = id;            
        }
        this.buildRequestForm();
    }

    showError(error: any): void {
        this.errorOccurred.emit(error);        
    }

    validateRequestForm(): void {
        this.requestForm.controls["orgSelector"].updateValueAndValidity();
        if (this.newRequest.organizationId !== this.requestForm.controls["orgSelector"].value) {
            this.applicationList = null;
            this.contactList = null;
            this.workOrderList = null;
            this.loadApplicationsForOrganization(this.requestForm.controls["orgSelector"].value);
            this.loadContactsForOrganization(this.requestForm.controls["orgSelector"].value);
            this.loadWorkOrdersForOrganization(this.requestForm.controls["orgSelector"].value);
        }
        this.newRequest.organizationId = this.requestForm.controls["orgSelector"].value;
    }   

    protected workOrderSelectionChanged(e: Event): void {
        const tempSelected = [];
		const rows = this.workOrderGridApi.getSelectedRows();
        for (let r = 0; r < rows.length; r++) {
            tempSelected.push((rows[r] as WorkOrderWithAssignments).id);
        }
        this.selectedWorkOrders = tempSelected;
    }    
}
